import { accountMutationHeaders, browserAccountStateSessionRepository } from "../account/account-state-session-repository.js";
import { FISHING_CONTEST_CATEGORIES, FISHING_CONTEST_SELECTION_CHANGE_EVENT, FISHING_CONTEST_VOTING_CLOSE_EVENT, decodeFishingContestCommandV1, normalizeFishingContestSelectionProjection } from "../fishing-contest/contract.js";
import { registerSearchableDropdown } from "./searchable-dropdown.js";

const sameSelection = (a, b) => Boolean(a && b)
  && a.region === b.region && a.weekStartsAt === b.weekStartsAt && a.weekEndsAt === b.weekEndsAt
  && a.itemIds.length === b.itemIds.length && a.itemIds.every((id, index) => id === b.itemIds[index]);
const emptyFish = () => Object.freeze(Object.fromEntries(FISHING_CONTEST_CATEGORIES.map(key => [key, null])));
const HTMLElementBase = globalThis.HTMLElement ?? class {};

// Datastar owns requests and server HTML. This host only connects the existing
// account/language authorities and the map/FishyDex's narrow selection contract.
export class FishyFishingContest extends HTMLElementBase {
  #lifecycle = null;
  #unsubscribe = null;
  #observer = null;
  #session = null;
  #language = null;
  #started = false;
  #ready = false;
  #scope = null;
  #revision = 0;
  #weekTimer = null;
  #selection = null;
  #publishedSelection = null;
  #selectedFish = emptyFish();
  #preparing = false;
  #votingOpener = null;

  connectedCallback() {
    if (this.#lifecycle) return;
    this.#lifecycle = new AbortController();
    this.#language = window.__fishystuffLanguage;
    this.#session = browserAccountStateSessionRepository();
    this.#unsubscribe = this.#session.subscribe(() => this.#syncScope());
    window.addEventListener(this.#language.event, () => this.#syncScope(), { signal: this.#lifecycle.signal });
    window.addEventListener("pageshow", event => { if (event.persisted) this.#syncScope(true); }, { signal: this.#lifecycle.signal });
    this.addEventListener("close", event => {
      // Native close is queued; a reopened dialog belongs to the next interaction.
      if (event.target !== this.querySelector("[data-contest-dialog]") || event.target.open) return;
      this.#closePickers();
      const opener = this.#votingOpener;
      this.#votingOpener = null;
      this.dispatchEvent(new CustomEvent(FISHING_CONTEST_VOTING_CLOSE_EVENT, { bubbles: true }));
      if (opener !== null) this.querySelector(`[data-contest-focus="${opener}"]`)?.focus();
    }, { capture: true, signal: this.#lifecycle.signal });
    // Observe only the server's bounded public projection, after its cohesive
    // morph. No Datastar events, signal proxy, catalog, or patch protocol enters JS.
    this.#observer = new MutationObserver(() => {
      const projection = this.querySelector(`#${this.id}-projection`);
      if (projection) this.acceptProjection(projection, Number(projection.dataset.contestRevision));
    });
    this.#observer.observe(this, { subtree: true, childList: true, attributes: true,
      attributeFilter: ["data-contest-selection", "data-contest-selected-fish", "data-contest-unavailable"] });
    const lifecycle = this.#lifecycle;
    void this.#language.ready.then(() => {
      if (this.#lifecycle !== lifecycle) return;
      this.#ready = true;
      this.#syncScope();
    });
    void this.#session.load();

  }

  start() { this.#started = true; this.#syncScope(); }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#lifecycle = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#weekTimer);
    this.closeVoting();
    this.#selection = null;
    this.#selectedFish = emptyFish();
    this.#scope = null;
    this.#started = false;
    this.#ready = false;
    this.#publish();
  }

  #syncScope(force = false) {
    if (!this.#lifecycle || !this.#started || !this.#ready) return;
    const language = this.#language.current();
    const session = this.#session.state();
    const next = { region: language.serverDataspace, lang: language.apiLang, locale: language.locale,
      identity: session.kind === "ready" ? session.accountId : session.kind,
      active: ["ready", "signedOut"].includes(session.kind) };
    if (!force && this.#scope && Object.keys(next).every(key => next[key] === this.#scope[key])) return;
    this.#scope = next;
    this.#revision += 1;
    this.#selection = null;
    this.#selectedFish = emptyFish();
    clearTimeout(this.#weekTimer);
    this.closeVoting();
    this.#publish();
    const projection = this.querySelector(`#${this.id}-projection`);
    if (!projection) throw new TypeError("Contest Datastar projection boundary is missing.");
    projection.dataset.contestRequest = String(this.#revision);
    this.querySelector("[data-contest-stream]").dispatchEvent(new CustomEvent("fishing-contest-scope", { detail: this.#view() }));
  }

  #view() {
    return { schemaVersion: 1, hostId: this.id, region: this.#scope.region, lang: this.#scope.lang,
      active: this.#scope.active, scopeRevision: this.#revision };
  }

  acceptProjection(root, revision = Number(root.dataset.contestRevision)) {
    if (!this.#scope || revision !== this.#revision || root.id !== `${this.id}-projection`) return;
    if (root.hasAttribute("data-contest-unavailable")) {
      this.#selection = null;
      this.#selectedFish = emptyFish();
      this.#publish();
      return;
    }
    if (!root.dataset.contestSelection) return;
    const selection = normalizeFishingContestSelectionProjection(JSON.parse(root.dataset.contestSelection));
    if (selection.region !== this.#scope.region) throw new TypeError("Contest projection region changed.");
    const fish = JSON.parse(root.dataset.contestSelectedFish);
    if (!fish || Object.keys(fish).length !== 3 || !FISHING_CONTEST_CATEGORIES.every(key => Object.hasOwn(fish, key))) {
      throw new TypeError("Contest selected-fish projection is invalid.");
    }
    for (const choice of Object.values(fish)) {
      if (choice !== null && (!Number.isSafeInteger(choice.itemId) || choice.itemId <= 0 || typeof choice.name !== "string"
        || !(choice.iconImage === null || typeof choice.iconImage === "string") || !selection.itemIds.includes(choice.itemId))) {
        throw new TypeError("Contest selected fish is invalid.");
      }
    }
    this.#selection = selection;
    this.#selectedFish = Object.freeze(fish);
    const ends = Date.parse(selection.weekEndsAt);
    clearTimeout(this.#weekTimer);
    if (ends > Date.now()) this.#weekTimer = setTimeout(() => this.#syncScope(true), ends - Date.now());
    this.#publish();
    for (const region of root.querySelectorAll("[data-contest-region]")) {
      if (!region.querySelector("fishy-searchable-dropdown")) this.#language.renderServiceRegionPanels(region);
    }
  }

  get selectedFish() { return this.#currentSelection() ? this.#selectedFish : emptyFish(); }
  #currentSelection() { return this.#selection && Date.parse(this.#selection.weekEndsAt) > Date.now() ? this.#selection : null; }
  get selectionProjection() {
    return this.#currentSelection() ?? normalizeFishingContestSelectionProjection({ schemaVersion: 1,
      region: this.#scope?.region ?? this.#language?.current().serverDataspace ?? window.__fishystuffLanguage.current().serverDataspace, weekStartsAt: null, weekEndsAt: null, itemIds: [] });
  }
  #publish() {
    const selection = this.selectionProjection;
    if (!sameSelection(selection, this.#publishedSelection)) {
      this.#publishedSelection = selection;
      this.dispatchEvent(new CustomEvent(FISHING_CONTEST_SELECTION_CHANGE_EVENT, { bubbles: true, composed: true, detail: selection }));
    }

  }

  openVoting(opener = document.activeElement) {
    const dialog = this.querySelector("[data-contest-dialog]");
    if (!dialog || dialog.open) return;
    this.#votingOpener = this.contains(opener) ? opener?.dataset.contestFocus ?? null : null;
    dialog.showModal();
  }
  openCandidatePicker(category, opener) {
    if (!FISHING_CONTEST_CATEGORIES.includes(category)) throw new TypeError("Contest picker category is invalid.");
    const picker = this.querySelector(`[data-contest-picker="${category}"]`);
    if (!picker) return;
    this.openVoting(opener);
    for (const other of this.querySelectorAll("fishy-searchable-dropdown")) {
      if (other !== picker) other.close();
    }
    picker.open();
  }
  #closePickers() {
    for (const picker of this.querySelectorAll("fishy-searchable-dropdown")) {
      picker.close();
    }
  }
  closeVoting() {
    this.#closePickers();
    for (const dialog of this.querySelectorAll("dialog[open]")) dialog.close();
  }
  prepareVote(category, itemId, weekStartsAt) { return this.#prepare({ kind: "vote", category, itemId, weekStartsAt }); }
  prepareDownvote(category, itemId, active, weekStartsAt) { return this.#prepare({ kind: "downvote", category, itemId, active, weekStartsAt }); }
  prepareConfirmation(itemId, weekStartsAt) { return this.#prepare({ kind: "confirm", itemId, weekStartsAt }); }
  async #prepare(command) {
    if (this.#preparing || !this.#scope?.active) return;
    this.#preparing = true;
    const revision = this.#revision;
    try {
      const session = await this.#session.refresh();
      if (!this.#lifecycle || revision !== this.#revision || !["ready", "signedOut"].includes(session.kind)) return;
      if (command.kind === "confirm" && session.kind !== "ready") return;
      const request = decodeFishingContestCommandV1({ schemaVersion: 1, region: this.#scope.region, ...command });
      const { Accept: _accept, "Content-Type": _contentType, ...headers } =
        session.kind === "ready" ? accountMutationHeaders(session) : {};
      // The fixed declarative action owns HTTP. Authorization stays in the shared
      // session repository and this one event, never a signal or durable record.
      this.querySelector("[data-contest-mutations]").dispatchEvent(new CustomEvent("fishing-contest-command", {
        detail: { request: { view: this.#view(), command: request }, headers },
      }));
    } finally { this.#preparing = false; }
  }
}

if (globalThis.customElements && !customElements.get("fishy-fishing-contest")) {
  registerSearchableDropdown();
  customElements.define("fishy-fishing-contest", FishyFishingContest);
}
